import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";

const FILTERED_VALUE = "[Filtered]";
const SIGNING_URL_TOKEN_PATTERN = /(\/s\/[^/?#]+\/[^/?#]+\/)[^/?#]+/g;
const SIGNING_START_URL_TOKEN_PATTERN =
  /(\/recipients\/[^/?#]+\/start\/)[^/?#]+/g;
const SIGNING_LINK_TOKEN_JSON_PATTERN =
  /("signing_link_token"\s*:\s*")[^"]*(")/gi;
const SIGNING_LINK_TOKEN_FORM_PATTERN = /(\bsigning_link_token=)[^&\s]+/gi;

export const redactSigningTokensFromString = (value: string) =>
  value
    .replace(SIGNING_URL_TOKEN_PATTERN, `$1${FILTERED_VALUE}`)
    .replace(SIGNING_START_URL_TOKEN_PATTERN, `$1${FILTERED_VALUE}`)
    .replace(SIGNING_LINK_TOKEN_JSON_PATTERN, `$1${FILTERED_VALUE}$2`)
    .replace(SIGNING_LINK_TOKEN_FORM_PATTERN, `$1${FILTERED_VALUE}`);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const redactSensitiveSentryValue = (
  value: unknown,
  seen = new WeakSet<object>(),
): unknown => {
  if (typeof value === "string") return redactSigningTokensFromString(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveSentryValue(item, seen));
  }

  if (!isRecord(value)) return value;
  if (seen.has(value)) return value;
  seen.add(value);

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      key.toLowerCase() === "signing_link_token"
        ? FILTERED_VALUE
        : redactSensitiveSentryValue(item, seen),
    ]),
  );
};

export const redactSigningTokensFromSentryEvent = (
  event: ErrorEvent,
): ErrorEvent => {
  return redactSensitiveSentryValue(event) as ErrorEvent;
};

const isDomLikelyTranslated = (): boolean => {
  if (typeof document === "undefined") return false;
  const htmlClassList = document.documentElement.classList;
  const hasTranslateClasses =
    htmlClassList.contains("translated-ltr") ||
    htmlClassList.contains("translated-rtl");
  if (hasTranslateClasses) return true;

  return Boolean(
    document.querySelector("font") ||
    document.querySelector(".goog-te-banner-frame") ||
    document.querySelector(".goog-te-menu-frame"),
  );
};

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
    release: import.meta.env.VITE_COMMIT_ID,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      const nextEvent = redactSigningTokensFromSentryEvent(event);
      nextEvent.tags = {
        ...nextEvent.tags,
        dom_is_translated: isDomLikelyTranslated() ? "true" : "false",
      };
      return nextEvent;
    },
  });
}
